import { IsLeapYear, dateStrToClientDate, getDateFields } from "Util/clientDate";
import { isValidHtml } from "./validateHtml";
import { THOUSAND_SEPARATOR_FORMATTER } from "Util/dataFormatters";
import { convertValueToString } from "Util/util";

import { KEYS, NAMESPACE } from "./i18n/validator.i18n";

export const VALIDATOR_TYPES = {
    max: "max",
    min: "min",
    dateRange: "dateRange",
    date: "date",
    integer: "integer",
    positiveInteger: "positiveInteger",
    nonNegativeInteger: "nonNegativeInteger",
    positiveNumber: "positiveNumber",
    nonNegativeNumber: "nonNegativeNumber",
    phone: "phone",
    email: "email",
    money: "money",
    decimal: "decimal",
    password: "password",
    required: "required", // Required to have a value
    requiredTrue: "requiredTrue", // Required to be true (ie. checkbox)
    minLength: "minLength",
    maxLength: "maxLength",
    custom: "custom",
    regEx: "regEx",
    crossCheck: "crossCheck",
    totp: "totp",
    multiEmail: "multiEmail",
    multiEmailOrTags: "multiEmailOrTags",
    url: "url",
    html: "html",
    alwaysValid: "alwaysValid"
};

const validatorMap = {
    [VALIDATOR_TYPES.min]: getMinValidator,
    [VALIDATOR_TYPES.max]: getMaxValidator,
    [VALIDATOR_TYPES.dateRange]: getDateRangeValidator,
    [VALIDATOR_TYPES.date]: getFormatValidator(null, null, KEYS.inValidDateFormatMsg, ($app) => {
        return function (value) {
            return isValidDate($app, value);
        };
    }),
    [VALIDATOR_TYPES.integer]: getFormatValidator(isValidInteger, null, KEYS.invalidIntegerMsg),
    [VALIDATOR_TYPES.positiveInteger]: getFormatValidator(isPositiveInteger, null, KEYS.invalidPositiveIntegerMsg),
    [VALIDATOR_TYPES.nonNegativeInteger]: getFormatValidator(
        isNonNegativeInteger,
        null,
        KEYS.invalidNonNegativeIntegerMsg
    ),
    [VALIDATOR_TYPES.positiveNumber]: getFormatValidator(isPositiveNumber, null, KEYS.invalidPositiveNumberMsg),
    [VALIDATOR_TYPES.nonNegativeNumber]: getFormatValidator(
        isNonNegativeNumber,
        null,
        KEYS.invalidNonNegativeNumberMsg
    ),
    [VALIDATOR_TYPES.phone]: getFormatValidator(isValidPhone, null, KEYS.invalidPhoneMsg),
    [VALIDATOR_TYPES.email]: getFormatValidator(isValidEmail, null, KEYS.inValidEmailFormatMsg),
    [VALIDATOR_TYPES.money]: getFormatValidator(isValidMoney, null, KEYS.invalidMoneyMsg),
    [VALIDATOR_TYPES.decimal]: getFormatValidator(isValidDecimal, null, KEYS.invalidDecimalMsg),
    [VALIDATOR_TYPES.password]: getFormatValidator(isValidPassword, null, KEYS.invalidPasswordMsg),
    [VALIDATOR_TYPES.crossCheck]: getFormatValidator(isValidCrossCheck, null, KEYS.invalidCrossCheckMsg),
    [VALIDATOR_TYPES.totp]: getFormatValidator(isValidTotp, null, KEYS.invalidTotpMsg),
    [VALIDATOR_TYPES.multiEmail]: getFormatValidator(isValidMultiEmail, null, KEYS.invalidMultiEmailMsg),
    [VALIDATOR_TYPES.multiEmailOrTags]: getMultiEmailOrTagValidator,
    [VALIDATOR_TYPES.required]: getRequiredValidator,
    [VALIDATOR_TYPES.requiredTrue]: getRequiredTrueValidator,
    [VALIDATOR_TYPES.minLength]: getMinLengthValidator,
    [VALIDATOR_TYPES.maxLength]: getMaxLengthValidator,
    [VALIDATOR_TYPES.custom]: getCustomValidator,
    [VALIDATOR_TYPES.regEx]: getRegExValidator,
    [VALIDATOR_TYPES.url]: getUrlValidator,
    [VALIDATOR_TYPES.html]: getHtmlValidator,
    [VALIDATOR_TYPES.alwaysValid]: getAlwaysValidValidator
};

const defaultValidatorOptions = {
    [VALIDATOR_TYPES.password]: { skipTrimValue: true }
};

export function getValidator($app, options) {
    if (Array.isArray(options)) {
        const validators = options.filter((o) => o != null).map((o) => getSingleValidator(o, $app));
        return {
            validate(value) {
                for (let i = 0; i < validators.length; i++) {
                    const validator = validators[i];
                    const result = validator.validate(value);
                    if (result.hasError) {
                        return result;
                    }
                }
                return { hasError: false };
            }
        };
    } else {
        return getSingleValidator(options, $app);
    }
}

function getSingleValidator(options, $app) {
    // allow options to be a string or object
    const type = options.type || options;

    // Convert a string-type options to an object so we can set any
    // default options
    if (typeof options === "string") {
        options = { type: type };
    }

    // Some validators have default options specified, but if they are
    // explicitly overridden by the options object, that value should win.
    const defaultOptions = defaultValidatorOptions[type] || {};
    options = { ...defaultOptions, ...options, $app };

    // By default, the value will be trimmed before validation. In some rare cases,
    // such as validating matching passwords, this may not be appropriate, so provide
    // an option to use the untrimmed value.
    const skipTrimValue = options && options.skipTrimValue;
    const validatorCreationFunc = validatorMap[type];
    if (!validatorCreationFunc) {
        throw new Error(`Unknown validator type: ${type}`);
    }
    const validatorObject = validatorCreationFunc(options);
    return {
        validate(value) {
            const stringVal = convertValueToString(value);
            const normalizedValue = skipTrimValue ? stringVal : stringVal && stringVal.trim();
            if (!validatorObject.validate(normalizedValue)) {
                //errorMsg might be a function that we need to invoke to get the error message
                const error =
                    typeof validatorObject.errorMsg === "function"
                        ? validatorObject.errorMsg()
                        : validatorObject.errorMsg;
                return {
                    hasError: true,
                    error,
                    type
                };
            }
            return {
                hasError: false
            };
        }
    };
}

function getCustomValidator({ validate, errorMsg }) {
    return {
        validate,
        errorMsg
    };
}

function getRegExValidator({ regEx, errorMsg }) {
    return {
        validate(value) {
            return regEx.test(value);
        },
        errorMsg: errorMsg
    };
}

function getMinLengthValidator({ minLength, overrideMsg }) {
    return {
        validate(value) {
            if (value != null) {
                const length = value.toString().length;
                return length >= minLength;
            }
            return true;
        },
        errorMsg: overrideMsg || `Please enter at least ${minLength} characters.`
    };
}

function getMaxLengthValidator({ maxLength, overrideMsg }) {
    return {
        validate(value) {
            if (value) {
                const length = value.toString().length;
                return length <= maxLength;
            }
            return true;
        },
        errorMsg: overrideMsg || `Please enter no more than ${maxLength} characters.`
    };
}

// Arguments:
// max: The maximum accepted value; the input value must be less than or can be equal to this value
// acceptThousandSeparated: If the input value is thousand-separated, the validation will not trip over the commas
// overrideMsg: A custom error message to replace the default defined in the code below
// useTwoDecimals: Use two decimals in the display of the max value in the error message
// useThousandSeparatorFormatter: Use the thousand-separated format for the max value in the error message
function getMaxValidator({ max, acceptThousandSeparated, overrideMsg, useTwoDecimals, useThousandSeparatorFormatter }) {
    let maxStr = useTwoDecimals && typeof max === "number" ? max.toFixed(2) : max + "";
    if (useThousandSeparatorFormatter) {
        maxStr = THOUSAND_SEPARATOR_FORMATTER.format(maxStr);
    }

    return {
        validate(value) {
            if (value) {
                if (acceptThousandSeparated) {
                    value = THOUSAND_SEPARATOR_FORMATTER.unformat(value);
                }
                const numVal = Number(value);
                return numVal <= max;
            }
            return true;
        },
        errorMsg: overrideMsg || `Please enter a value less than or equal to ${maxStr}`
    };
}

// Arguments:
// min: The minimum accepted value; the input value must be greater than or can be equal to this value
// acceptThousandSeparated: If the input value is thousand-separated, the validation will not trip over the commas
// overrideMsg: A custom error message to replace the default defined in the code below
// useTwoDecimals: Use two decimals in the display of the min value in the error message
// useThousandSeparatorFormatter: Use the thousand-separated format for the min value in the error message
function getMinValidator({ min, acceptThousandSeparated, overrideMsg, useTwoDecimals, useThousandSeparatorFormatter }) {
    let minStr = useTwoDecimals && typeof min === "number" ? min.toFixed(2) : min + "";
    if (useThousandSeparatorFormatter) {
        minStr = THOUSAND_SEPARATOR_FORMATTER.format(minStr);
    }

    return {
        validate(value) {
            if (value) {
                if (acceptThousandSeparated) {
                    value = THOUSAND_SEPARATOR_FORMATTER.unformat(value);
                }
                const numVal = Number(value);
                return numVal >= min;
            }
            return true;
        },
        errorMsg: overrideMsg || `Please enter a value greater than or equal to ${minStr}`
    };
}

function getDateRangeValidator({ $app, earlyStr, lateStr, overrideMsg }) {
    const early = dateStrToClientDate($app, earlyStr);
    const late = dateStrToClientDate($app, lateStr);

    let defaultMessage = "";

    if (earlyStr && lateStr) {
        defaultMessage = `Date must be no earlier than ${earlyStr} and no later than ${lateStr}`;
    } else if (earlyStr && !lateStr) {
        defaultMessage = `Date must be no earlier than ${earlyStr}`;
    } else if (!earlyStr && lateStr) {
        defaultMessage = `Date must be no later than ${lateStr}`;
    }

    return {
        validate(value) {
            if (value) {
                const dateVal = dateStrToClientDate($app, value);
                return !dateVal || ((!early || dateVal.isOnOrAfter(early)) && (!late || late.isOnOrAfter(dateVal)));
            }
            return true;
        },
        errorMsg: overrideMsg || defaultMessage
    };
}

function getMultiEmailOrTagValidator({
    $app,
    emailFieldNames,
    userFieldNames,
    builtInFields,
    onlySingleEmail,
    templateTagsOnly
}) {
    const emailFieldNamesLower = (emailFieldNames && emailFieldNames.map((name) => name.toLowerCase())) || [];
    const emailFieldNamesSet = new Set(emailFieldNamesLower);
    const emailTagRegex = new RegExp(/^{\s*data:(.*)}$/, "i");

    const userFieldNamesLower = (userFieldNames && userFieldNames.map((name) => name.toLowerCase())) || [];
    const userFieldNamesSet = new Set(userFieldNamesLower);
    const userTagRegex = new RegExp(/^{\s*email:(.*)}$/, "i");

    // Built-in field tags should be case-insensitive
    const builtInFieldsSet = new Set(builtInFields.map((name) => name.toLowerCase()));
    const builtInFieldTagRegex = new RegExp(/^{\s*(.*)\s*}$/, "i");

    function _isValidEmailOrTag(item) {
        if (!templateTagsOnly && isValidEmail(item)) {
            return true;
        }

        const emailTagMatch = emailTagRegex.exec(item);
        if (emailTagMatch) {
            return emailTagMatch.length === 2 && emailFieldNamesSet.has(emailTagMatch[1].trim().toLowerCase());
        }

        const userTagMatch = userTagRegex.exec(item);
        if (userTagMatch) {
            return userTagMatch.length === 2 && userFieldNamesSet.has(userTagMatch[1].trim().toLowerCase());
        }

        const builtInFieldTagMatch = builtInFieldTagRegex.exec(item);
        if (builtInFieldTagMatch) {
            return (
                builtInFieldTagMatch.length === 2 && builtInFieldsSet.has(builtInFieldTagMatch[1].trim().toLowerCase())
            );
        }
    }
    /* eslint-disable max-len */
    let errorMsg = getLocalizedMsgFromKey($app, KEYS.invalidMultiEmailOrTagsMsg);
    if (onlySingleEmail && templateTagsOnly) {
        errorMsg = getLocalizedMsgFromKey($app, KEYS.invalidSingleTagsOnlyMsg);
    } else if (onlySingleEmail) {
        errorMsg = getLocalizedMsgFromKey($app, KEYS.invalidSingleEmailOrTagsMsg);
    } else if (templateTagsOnly) {
        // this has no use case, there is no instance where you can have multiple template tag-only emails.
        // Putting it in for posterity but not bothering to localize since it's not used
        errorMsg =
            "Please specify a valid data field (e.g. {data:XXXXX} or {AssigneeEmail} or {ProjectOwnerEmail}). Separate multiple addresses with commas or semicolons.";
    }
    /* eslint-enable max-len */

    return {
        validate(value) {
            const items = _splitCommaSemicolonSeparatedString(value);
            if (onlySingleEmail && items.length > 1) {
                return false;
            }

            for (const item of items) {
                if (!_isValidEmailOrTag(item)) {
                    return false;
                }
            }
            return true;
        },
        errorMsg
    };
}

function getUrlValidator({ overrideMsg }) {
    return {
        validate(value) {
            if (!value) {
                return true;
            }

            if (value.startsWith("mailto:")) {
                return isValidEmail(value.substring(7)); // don't include the directive, just the address
            }

            let url;
            try {
                url = new URL(value);
            } catch (_) {
                return false;
            }

            return (url.protocol === "http:" || url.protocol === "https:") && url.hostname;
        },
        errorMsg:
            overrideMsg ||
            "The URL you entered is not recognized and has been disallowed. URLs must be valid links " +
                "that begin with http:// or https://, or valid email addresses preceded by mailto:."
    };
}

function getHtmlValidator({ overrideMsg, overrideAllowList }) {
    return {
        validate(value) {
            return isValidHtml(value, overrideAllowList);
        },
        errorMsg:
            overrideMsg ||
            "The entered HTML contains potentially unsafe elements that will be removed when it gets saved."
    };
}

function getRequiredValidator({ overrideMsg, $app }) {
    //Note: value parameter is passed to this method as a string from getSingleValidator
    //If passing an empty array [].ToString() === "" as therefore will be considered invalid
    return {
        validate(value) {
            return value !== null && value !== undefined && value !== "";
        },
        errorMsg:
            overrideMsg ??
            (() => {
                return getLocalizedMsgFromKey($app, KEYS.requiredMsg);
            })
    };
}

function getRequiredTrueValidator({ overrideMsg, $app }) {
    return {
        validate(value) {
            // All inputs are stringified before being handed to individual validators
            return value === "true";
        },
        errorMsg:
            overrideMsg ??
            (() => {
                return getLocalizedMsgFromKey($app, KEYS.requiredTrueMsg);
            })
    };
}

function getFormatValidator(validationFunc, defaultMessage, defaultMessageKey, validationFactoryFn) {
    return function ({ overrideMsg, acceptThousandSeparated, $app }) {
        if (!validationFunc && validationFactoryFn) {
            validationFunc = validationFactoryFn($app);
        }
        return {
            validate(value) {
                if (acceptThousandSeparated) {
                    value = THOUSAND_SEPARATOR_FORMATTER.unformat(value);
                }
                return !value || validationFunc(value);
            },
            errorMsg:
                overrideMsg ??
                defaultMessage ??
                (() => {
                    return getLocalizedMsgFromKey($app, defaultMessageKey);
                })
        };
    };
}

// As of 3/27/2023 there are only two options for getLocalizedMsgFromKey.  If we need to add additional handling of more than
// a couple of messages with value inputs, then it makes sense to add a 4th parameter to getFormatValidator that accepts a
// function instead of just a key.  There will be a function for every message that requires a different key with its own
// set of input parameters.
function getLocalizedMsgFromKey($app, key) {
    if (key === KEYS.inValidDateFormatMsg) {
        return $app.res({
            namespace: NAMESPACE,
            key: KEYS.inValidDateFormatMsg,
            values: { dateFormat: $app.DateCultureMessageInfo.ShortDateFormat }
        });
    }

    return $app.res({
        namespace: NAMESPACE,
        key
    });
}

/**
 * Returns a "placeholder" validator. This is useful in cases where
 * we have a validator that should be applied conditionally. By including
 * this validator as part of a compound validator with one or more conditional
 * validators, we can ensure that the input gets treated as though it always
 * has a validator even though its other validators may conditionally
 * appear and disappear. This is only necessary to include if the compound
 * validator doesn't include any other unconditional validators.
 */
function getAlwaysValidValidator() {
    return {
        validate() {
            return true;
        },
        errorMsg: ""
    };
}

export function isValidDate($app, value, year, month, day) {
    if (arguments.length == 2) {
        [year, month, day] = getDateFields($app, value);
    }
    month--;

    const monthToDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (IsLeapYear(year)) {
        monthToDays[1]++; // February
    }

    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return false;
    } else if (month < 0 || month > 11) {
        return false;
    }

    return day <= monthToDays[month];
}

export function isValidInteger(intStr) {
    return /^-?\d+$/.test(intStr);
}
function isPositiveInteger(intStr) {
    return isValidInteger(intStr) && Number(intStr) > 0;
}
function isNonNegativeInteger(intStr) {
    return isValidInteger(intStr) && Number(intStr) >= 0;
}
function isPositiveNumber(numStr) {
    return /\d+(\.\d+)?/.test(numStr) && Number(numStr) > 0;
}
function isNonNegativeNumber(numStr) {
    return /\d+(\.\d+)?/.test(numStr) && Number(numStr) >= 0;
}
function isValidPhone(phoneStr) {
    return /^\(\d{3}\) \d{3}-\d{4}$/.test(phoneStr);
}
function isValidEmail(emailStr) {
    return /^[^@\s]+@\w((\w|-\w|\.\w)*)(\.\w{2,})$/.test(emailStr);
}
function isValidMoney(moneyStr) {
    return /^-?\d{0,}(\.\d{2})?$/.test(moneyStr);
}
function isValidDecimal(decStr) {
    return /^-?\d*(\.\d+)?$/.test(decStr);
}

function isValidPassword(passwordStr) {
    // Password cannot start or end with spaces
    if (passwordStr !== passwordStr.trim()) {
        return false;
    }
    return passwordStr.match(/.*(?=.{8,})(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]).*/);
}

// Validate the cross-check codes that we generate for actions like
// changing an account's email address. Should be 6 characters: letters,
// digits, and '$' or '#'
function isValidCrossCheck(crossCheckStr) {
    return /^[A-Za-z0-9$#]{6}$/.test(crossCheckStr);
}

// Check if the value is a valid 6-digit TOTP (Time-based one-time password)
// https://en.wikipedia.org/wiki/Time-based_One-Time_Password
function isValidTotp(totpStr) {
    return /^\d{6}$/.test(totpStr);
}

function isValidMultiEmail(multiEmailStr) {
    if (!multiEmailStr) {
        return true;
    }
    const emails = _splitCommaSemicolonSeparatedString(multiEmailStr);
    return emails.every((e) => {
        return isValidEmail(e);
    });
}

/**
 * Split a comma or semicolon-separated string into individual
 * items, trim all, remove any empty entries
 */
function _splitCommaSemicolonSeparatedString(separatedString) {
    if (!separatedString) {
        return [];
    }

    return separatedString
        .split(/[;,]+/)
        .map((s) => s.trim())
        .filter((s) => s !== "");
}
